import { ReportingTeacherUiHeader } from 'teacher-ui-client/components/reporting/teacher-ui-header'
import { ReportingDashboardControls } from 'teacher-ui-client/components/reporting/dashboard-controls'
import { t } from 'ember-intl'
import { ContentTile } from 'teacher-ui-client/components/tiles/content'
import DataTableOperations from '@blakeelearning/data-tables/components/data-table-operations'
import { StickyHeaderTable } from 'teacher-ui-client/components/tables/sticky-header'
import type { MsDashboardTableRoute } from 'teacher-ui-client/reporting/ui-scope/maths/dashboard/table/route'
import { array } from '@ember/helper'
import { ExportType } from 'teacher-ui-client/components/reporting/export-select'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { Tooltip } from 'teacher-ui-client/components/tooltip'
import { StickyHeaderColumnTable } from 'teacher-ui-client/components/tables/sticky-header-column'
import { on } from '@ember/modifier'
import { fn } from '@ember/helper'
import { eq } from 'ember-truth-helpers'
import { formatValue } from 'teacher-ui-client/utils/format-value'
import { formatDuration } from 'teacher-ui-client/utils/format-duration'
import { gradeName } from 'teacher-ui-client/helpers/grade-name'
import { NoDataMessage } from 'teacher-ui-client/components/no-data-message'
import RouteTemplate from 'ember-route-template'
import { StickyColumnTable } from 'teacher-ui-client/components/tables/sticky-column'
import Component from '@glimmer/component'
import type RouterService from '@ember/routing/router-service'
import type SessionService from 'teacher-ui-client/services/session'
import { service } from '@ember/service'
import { tracked } from '@glimmer/tracking'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import { isPresent } from '@ember/utils'
import { statefulSort, type TableAction } from 'teacher-ui-client/utils/giraffe/stateful-sort'
import type { PhosphorIconName } from 'phosphor-icons'

interface Signature {
  Args: {
    model: ModelFor<MsDashboardTableRoute>
  }
}
export class MathsDashboardTablePage extends Component<Signature> {
  @service router!: RouterService
  @service session!: SessionService

  // These are set by statefulSort action
  @tracked currentSortIcon: PhosphorIconName = 'phosphor-caret-up-down-fill'
  @tracked currentSortColumn?: string

  get isSubco() {
    return this.session.currentTeacher.subcoFor(SubscriptionType.Maths)
  }

  get studentColspan() {
    return this.isSubco ? 5 : 3
  }
  get totalColspan() {
    return this.isSubco ? 3 : 1
  }

  get isSummaryDataPresent() {
    return isPresent(this.args.model.summary)
  }

  get summaryRow() {
    const totals = this.args.model?.summary?.school

    return {
      averageUsageScore: totals?.average_usage_score,
      awardsEarnedBronze: totals?.awards_earned_bronze,
      awardsEarnedGold: totals?.awards_earned_gold,
      awardsEarnedSilver: totals?.awards_earned_silver,
      drivingTestsAttempted: totals?.driving_tests_attempted,
      drivingTestsCompleted: totals?.driving_tests_completed,
      lessonsCompleted: totals?.lessons_completed,
      mapQuizzesAttempted: totals?.map_quizzes_attempted,
      mapQuizzesAverageScorePercent: totals?.map_quizzes_average_score_percent,
      mapQuizzesTaken: totals?.map_quizzes_taken,
      sprintsMasteredTotal: totals?.sprints_mastered_total,
      skillsMasteredTotal: totals?.skills_mastered_total,
      studentCount: totals?.student_count,
      totalTime: totals?.total_time,
    }
  }

  get rows() {
    const statsData = this.args.model?.summary?.students

    return this.args.model.scopedStudents.map((student) => {
      const foundStatsDataForStudent = statsData?.find((item) => String(item?.student_id) === student.id) ?? {}
      return {
        ...foundStatsDataForStudent,
        firstName: student.firstName,
        lastName: student.lastName,
        gradePosition: student.gradePosition,
        studentId: student.id,
        studentExternalId: student.externalId,
        teacherName: student
          .teachersFor(SubscriptionType.Maths)
          .map((teacher) => teacher.fullName)
          .join(', '),
        schoolClasses: student
          .schoolClassesFor(SubscriptionType.Maths)
          .map((schoolClass) => schoolClass.name)
          .join(', '),
      }
    })
  }

  statefulSort = (tableAction: TableAction, tableActionOperation: string, tableActionKey: string) => {
    return statefulSort(this, tableAction, tableActionOperation, tableActionKey)
  }

  transitionToStudentRoute = (studentId: string) => {
    void this.router.transitionTo('reporting.ui-scope.maths.student-summary.table', studentId)
  }

  <template>
    <ReportingTeacherUiHeader @title={{t "reporting.dashboardTitles.maths"}} />
    <ReportingDashboardControls
      @students={{@model.scopedStudents}}
      @routePrefix="reporting.ui-scope.maths.dashboard"
      @exportOptions={{array ExportType.CSV}}
      @isDataPresent={{this.isSummaryDataPresent}}
      @studentRouteName="reporting.ui-scope.maths.student-summary.table"
    />
    {{#if this.isSummaryDataPresent}}
      <ContentTile>
        <div class="max-h-192 overflow-auto print:max-h-[unset] print:overflow-visible">
          <DataTableOperations @data={{this.rows}} as |transformedData tableAction|>
            <table
              class="table-print-zoom-1/2 relative print:divide-y print:divide-black"
              data-test-dashboard-table
              exportable
            >
              <thead class="font-semibold print:divide-y print:divide-black">
                {{! Using box shadow, we can get a border between the header rows, even with the sticky header }}
                <tr
                  class="bg-ms-green-150 divide-x divide-white text-center text-black shadow-[inset_0_-1px_0] shadow-white print:divide-black"
                >
                  <StickyHeaderTable class="p-3" colspan={{this.studentColspan}}>
                    {{t "reporting.dashboardTables.students"}}
                  </StickyHeaderTable>
                  {{! for studentExternalId column }}
                  <th class="hidden" />
                  <StickyHeaderTable class="p-3" colspan="3">
                    {{t "reporting.dashboardTables.lessonsCompleted.maths"}}
                  </StickyHeaderTable>
                  <StickyHeaderTable class="p-3">
                    {{t "reporting.dashboardTables.skillsMastered"}}
                  </StickyHeaderTable>
                  <StickyHeaderTable class="p-3" colspan="2">
                    {{t "reporting.dashboardTables.drivingTests"}}
                  </StickyHeaderTable>
                  <StickyHeaderTable class="p-3" colspan="2">
                    {{t "reporting.dashboardTables.quizzesCompleted.maths"}}
                  </StickyHeaderTable>
                  <StickyHeaderTable class="p-3">
                    {{t "reporting.dashboardTables.mentalMathsFactFluency" htmlSafe=true}}
                  </StickyHeaderTable>
                  <StickyHeaderTable class="p-3" colspan="3">
                    {{t "reporting.dashboardTables.certificates"}}
                  </StickyHeaderTable>
                  <StickyHeaderTable class="p-3 whitespace-nowrap">
                    {{t "reporting.dashboardTables.usage"}}
                    <span class="print:hidden">
                      <PhosphorIcon @icon="phosphor-info-fill" class="text-dusty-black-500 cursor-pointer" />
                      <Tooltip @text={{t "reporting.dashboardTables.usageTooltip.maths"}} role="presentation" />
                    </span>
                  </StickyHeaderTable>
                  <StickyHeaderTable class="p-3">
                    {{t "reporting.dashboardTables.timeInProgram"}}
                  </StickyHeaderTable>
                </tr>
                <tr
                  class="bg-ms-green-100 divide-x divide-white text-sm text-black shadow-[inset_0_-1px_0] shadow-white print:divide-black"
                >
                  <StickyHeaderColumnTable
                    class="min-w-40 p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByValue" "firstName")}}
                    role="button"
                  >
                    <div class="flex items-center justify-between gap-1">
                      {{t "reporting.dashboardTables.firstName" htmlSafe=true}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "firstName")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </StickyHeaderColumnTable>
                  <StickyHeaderColumnTable
                    class="min-w-40 p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByValue" "lastName")}}
                    role="button"
                  >
                    <div class="flex items-center justify-between gap-1">
                      {{t "reporting.dashboardTables.lastName" htmlSafe=true}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "lastName")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </StickyHeaderColumnTable>
                  <th class="hidden">
                    {{t "reporting.dashboardTables.studentExternalId"}}
                  </th>
                  <StickyHeaderTable
                    class="min-w-40 p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByValue" "schoolClasses")}}
                    role="button"
                  >
                    <div class="flex items-center justify-between gap-1">
                      {{t "reporting.dashboardTables.classes"}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "schoolClasses")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </StickyHeaderTable>
                  {{#if this.isSubco}}
                    <StickyHeaderTable
                      data-test-teacher-name
                      class="min-w-40 p-3 select-none"
                      {{on "click" (fn this.statefulSort tableAction "sortByValue" "teacherName")}}
                      role="button"
                    >
                      <div class="flex items-center justify-between gap-1">
                        {{t "reporting.dashboardTables.teacher"}}
                        <PhosphorIcon
                          @icon={{if
                            (eq this.currentSortColumn "teacherName")
                            this.currentSortIcon
                            "phosphor-caret-up-down-fill"
                          }}
                          class="shrink-0 print:hidden"
                          role="presentation"
                        />
                      </div>
                    </StickyHeaderTable>
                    <StickyHeaderTable
                      class="p-3 select-none"
                      {{on "click" (fn this.statefulSort tableAction "sortByGrade" "gradePosition")}}
                      role="button"
                    >
                      <div class="flex items-center justify-between gap-1">
                        {{t "grade.label"}}
                        <PhosphorIcon
                          @icon={{if
                            (eq this.currentSortColumn "gradePosition")
                            this.currentSortIcon
                            "phosphor-caret-up-down-fill"
                          }}
                          class="shrink-0 print:hidden"
                          role="presentation"
                        />
                      </div>
                    </StickyHeaderTable>
                  {{/if}}
                  <StickyHeaderTable
                    class="p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByNumber" "initial_lesson_number")}}
                    role="button"
                  >
                    <div class="flex items-center justify-between gap-1">
                      {{t "reporting.dashboardTables.initialLesson.maths" htmlSafe=true}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "initial_lesson_number")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </StickyHeaderTable>
                  <StickyHeaderTable
                    class="p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByNumber" "current_lesson_number")}}
                    role="button"
                  >
                    <div class="flex items-center justify-between gap-1">
                      {{t "reporting.dashboardTables.highestLessonCompleted.maths" htmlSafe=true}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "current_lesson_number")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </StickyHeaderTable>
                  <StickyHeaderTable
                    class="relative p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByNumber" "lessons_completed")}}
                    role="button"
                  >
                    <div class="flex items-center gap-1">
                      {{t "reporting.dashboardTables.totalCompleted"}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "lessons_completed")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="ml-auto shrink-0 print:hidden"
                        role="presentation"
                      />
                      {{#if this.isSubco}}
                        <span class="shrink-0 print:hidden" role="presentation">
                          <PhosphorIcon
                            @icon="phosphor-info-fill"
                            class="text-dusty-black-500 cursor-pointer"
                            role="presentation"
                          />
                          <Tooltip @text={{t "reporting.dashboardToolTipTotals"}} role="presentation" />
                        </span>
                      {{/if}}
                    </div>
                  </StickyHeaderTable>
                  <StickyHeaderTable
                    class="p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByNumber" "skills_mastered")}}
                    role="button"
                  >
                    <div class="text-right">
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "skills_mastered")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </StickyHeaderTable>
                  <StickyHeaderTable
                    class="p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByNumber" "driving_tests_completed")}}
                    role="button"
                  >
                    <div class="flex items-center justify-between gap-1">
                      {{t "reporting.dashboardTables.testsMastered" htmlSafe=true}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "driving_tests_completed")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </StickyHeaderTable>
                  <StickyHeaderTable
                    class="p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByNumber" "driving_tests_attempted")}}
                    role="button"
                  >
                    <div class="flex items-center justify-between gap-1">
                      {{t "reporting.dashboardTables.totalAttempts" htmlSafe=true}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "driving_tests_attempted")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </StickyHeaderTable>
                  <StickyHeaderTable
                    class="p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByNumber" "map_quizzes_taken")}}
                    role="button"
                  >
                    <div class="flex items-center justify-between gap-1">
                      {{t "reporting.dashboardTables.numberOfMapQuizzesTaken" htmlSafe=true}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "map_quizzes_taken")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </StickyHeaderTable>
                  <StickyHeaderTable
                    class="p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByNumber" "map_quizzes_average_score_percent")}}
                    role="button"
                  >
                    <div class="flex items-center justify-between gap-1">
                      {{t "reporting.dashboardTables.averageLessonQuizScore.maths" htmlSafe=true}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "map_quizzes_average_score_percent")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </StickyHeaderTable>
                  <StickyHeaderTable
                    class="p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByNumber" "sprints_mastered")}}
                    role="button"
                  >
                    <div class="flex items-center justify-between gap-1">
                      {{t "reporting.dashboardTables.sprintsMastered" htmlSafe=true}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "sprints_mastered")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </StickyHeaderTable>
                  <StickyHeaderTable
                    class="p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByNumber" "awards_earned_gold")}}
                    role="button"
                  >
                    <div class="flex items-center justify-between gap-1">
                      {{t "reporting.dashboardTables.gold"}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "awards_earned_gold")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </StickyHeaderTable>
                  <StickyHeaderTable
                    class="p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByNumber" "awards_earned_silver")}}
                    role="button"
                  >
                    <div class="flex items-center justify-between gap-1">
                      {{t "reporting.dashboardTables.silver"}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "awards_earned_silver")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </StickyHeaderTable>
                  <StickyHeaderTable
                    class="p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByNumber" "awards_earned_bronze")}}
                    role="button"
                  >
                    <div class="flex items-center justify-between gap-1">
                      {{t "reporting.dashboardTables.bronze"}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "awards_earned_bronze")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </StickyHeaderTable>
                  <StickyHeaderTable
                    class="p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByNumber" "usage_score")}}
                    role="button"
                  >
                    <div class="text-right">
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "usage_score")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </StickyHeaderTable>
                  <StickyHeaderTable
                    class="p-3 select-none"
                    {{on "click" (fn this.statefulSort tableAction "sortByNumber" "total_time")}}
                    role="button"
                  >
                    <div class="flex items-center justify-between gap-1">
                      {{t "reporting.dashboardTables.totalHours"}}
                      <PhosphorIcon
                        @icon={{if
                          (eq this.currentSortColumn "total_time")
                          this.currentSortIcon
                          "phosphor-caret-up-down-fill"
                        }}
                        class="shrink-0 print:hidden"
                        role="presentation"
                      />
                    </div>
                  </StickyHeaderTable>
                </tr>
                <tr class="bg-ms-green-100 text-black shadow-[inset_0_-1px_0] shadow-white" data-test-summary-row>
                  <StickyHeaderColumnTable class="p-3" colspan={{2}}>
                    {{t "reporting.dashboardTables.totals"}}
                  </StickyHeaderColumnTable>
                  <StickyHeaderTable colspan={{this.totalColspan}} />
                  {{! for studentExternalId column }}
                  <th class="hidden" />
                  <StickyHeaderTable class="p-3">
                    -
                  </StickyHeaderTable>
                  <StickyHeaderTable class="p-3">
                    -
                  </StickyHeaderTable>
                  <StickyHeaderTable class="p-3">
                    {{formatValue this.summaryRow.lessonsCompleted}}
                  </StickyHeaderTable>
                  <StickyHeaderTable class="p-3">
                    {{formatValue this.summaryRow.skillsMasteredTotal}}
                  </StickyHeaderTable>
                  <StickyHeaderTable class="p-3">
                    {{formatValue this.summaryRow.drivingTestsCompleted}}
                  </StickyHeaderTable>
                  <StickyHeaderTable class="p-3">
                    {{formatValue this.summaryRow.drivingTestsAttempted}}
                  </StickyHeaderTable>
                  <StickyHeaderTable class="p-3">
                    {{formatValue this.summaryRow.mapQuizzesTaken}}
                  </StickyHeaderTable>
                  <StickyHeaderTable class="p-3">
                    {{formatValue this.summaryRow.mapQuizzesAverageScorePercent append="%"}}
                  </StickyHeaderTable>
                  <StickyHeaderTable class="p-3">
                    {{formatValue this.summaryRow.sprintsMasteredTotal}}
                  </StickyHeaderTable>
                  <StickyHeaderTable class="p-3">
                    {{formatValue this.summaryRow.awardsEarnedGold}}
                  </StickyHeaderTable>
                  <StickyHeaderTable class="p-3">
                    {{formatValue this.summaryRow.awardsEarnedSilver}}
                  </StickyHeaderTable>
                  <StickyHeaderTable class="p-3">
                    {{formatValue this.summaryRow.awardsEarnedBronze}}
                  </StickyHeaderTable>
                  <StickyHeaderTable class="p-3">
                    {{formatValue this.summaryRow.averageUsageScore}}
                  </StickyHeaderTable>
                  <StickyHeaderTable class="p-3">
                    {{formatDuration this.summaryRow.totalTime "-"}}
                  </StickyHeaderTable>
                </tr>
              </thead>
              <tbody class="print:divide-dusty-black-100 print:divide-y">
                {{#each transformedData as |row index|}}
                  {{! template-lint-disable no-invalid-interactive }}
                  <tr
                    data-test-table-row={{index}}
                    class="even:bg-ms-green-50 hover:bg-ms-green-100 cursor-pointer bg-white"
                    {{on "click" (fn this.transitionToStudentRoute row.studentId)}}
                  >
                    <StickyColumnTable class="text-ms-green-300 p-3 font-bold wrap-anywhere">
                      {{row.firstName}}
                    </StickyColumnTable>
                    <StickyColumnTable class="text-ms-green-300 p-3 font-bold wrap-anywhere">
                      {{row.lastName}}
                    </StickyColumnTable>
                    <td class="hidden">
                      {{row.studentExternalId}}
                    </td>
                    <td class="p-3">
                      {{row.schoolClasses}}
                    </td>
                    {{#if this.isSubco}}
                      <td class="p-3">
                        {{row.teacherName}}
                      </td>
                      <td class="p-3">
                        {{gradeName row.gradePosition}}
                      </td>
                    {{/if}}
                    <td class="p-3">
                      {{formatValue row.initial_lesson_number}}
                    </td>
                    <td class="p-3">
                      {{formatValue row.current_lesson_number}}
                    </td>
                    <td class="p-3">
                      {{formatValue row.lessons_completed}}
                    </td>
                    <td class="p-3">
                      {{formatValue row.skills_mastered}}
                    </td>
                    <td class="p-3">
                      {{formatValue row.driving_tests_completed}}
                    </td>
                    <td class="p-3">
                      {{formatValue row.driving_tests_attempted}}
                    </td>
                    <td class="p-3">
                      {{formatValue row.map_quizzes_taken}}
                    </td>
                    <td class="p-3">
                      {{formatValue row.map_quizzes_average_score_percent append="%"}}
                    </td>
                    <td class="p-3">
                      {{formatValue row.sprints_mastered}}
                    </td>
                    <td class="p-3">
                      {{formatValue row.awards_earned_gold}}
                    </td>
                    <td class="p-3">
                      {{formatValue row.awards_earned_silver}}
                    </td>
                    <td class="p-3">
                      {{formatValue row.awards_earned_bronze}}
                    </td>
                    <td class="p-3">
                      {{formatValue row.usage_score}}
                    </td>
                    <td class="p-3">
                      {{formatDuration row.total_time "-"}}
                    </td>
                  </tr>
                {{/each}}
              </tbody>
            </table>
          </DataTableOperations>
        </div>
      </ContentTile>
    {{else}}
      <NoDataMessage />
    {{/if}}
  </template>
}

export default RouteTemplate(MathsDashboardTablePage)
